/**
 * CabLab — CNC Feeds & Speeds (v2 — corrected for engraving / micro-tip tools)
 * Metric-first, UK-oriented defaults.
 *
 * Core formulas:
 *  - Feed (mm/min) = ChipLoad (mm/tooth) * Flutes * RPM
 *  - RPM (from surface speed) = (SurfaceSpeed(m/min) * 1000) / (pi * Diameter(mm))
 *
 * Notes:
 *  - For V-bits / engraving tools, effective cutting diameter at depth can be tiny.
 *    Chipload must be capped aggressively, and recommendations should be based on effective diameter,
 *    not the shank diameter.
 *  - DOM contract: uses the existing data-* attributes currently in your tool markup.
 */
(function () {
  "use strict";

  var PI = Math.PI;
  var MM_PER_IN = 25.4;

  // ---------------- utils ----------------
  function safeNum(v, fallback) {
    var n = parseFloat(v);
    return isFinite(n) ? n : (fallback || 0);
  }
  function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
  }
  function fmt(n, d) {
    if (!isFinite(n)) return "—";
    return n.toFixed(d == null ? 2 : d);
  }
  function mmToIn(mm) { return mm / MM_PER_IN; }
  function inToMm(i) { return i * MM_PER_IN; }
  function mmMinToInMin(mmMin) { return mmMin / MM_PER_IN; }

  // ---------------- physics ----------------
  function rpmFromSurfaceSpeed(surfaceSpeedMmin, diameterMm) {
    if (!diameterMm || diameterMm <= 0) return 0;
    return (surfaceSpeedMmin * 1000) / (PI * diameterMm);
  }
  function surfaceSpeedFromRpm(rpm, diameterMm) {
    if (!diameterMm || diameterMm <= 0) return 0;
    return (rpm * PI * diameterMm) / 1000;
  }
  function feedRateMmMin(chipLoadMm, flutes, rpm) {
    return chipLoadMm * flutes * rpm;
  }
  function mrr(feedMmMin, stepdownMm, stepoverPct) {
    return feedMmMin * stepdownMm * (stepoverPct / 100);
  }

  // ---------------- material presets ----------------
  // These are conservative but realistic for ROUTING (panel goods / timber).
  // Micro-tip engraving tools will override chipload recommendations automatically.
  var MATERIALS = {
    mdf: {
      name: "MDF (Standard)",
      chipMin: 0.08, chipMax: 0.22, chipDefault: 0.14,
      vMin: 220, vMax: 420, vDefault: 320,
      heatSensitive: true, finishSensitive: true
    },
    mdf_finsa: {
      name: "MDF (Finsa / High Quality)",
      chipMin: 0.08, chipMax: 0.24, chipDefault: 0.15,
      vMin: 240, vMax: 450, vDefault: 340,
      heatSensitive: true, finishSensitive: true
    },
    mfc: {
      name: "MFC (Melamine Faced)",
      chipMin: 0.07, chipMax: 0.20, chipDefault: 0.12,
      vMin: 240, vMax: 460, vDefault: 360,
      heatSensitive: true, finishSensitive: true
    },
    birch_ply: {
      name: "Birch Plywood",
      chipMin: 0.09, chipMax: 0.25, chipDefault: 0.16,
      vMin: 260, vMax: 520, vDefault: 400,
      heatSensitive: false, finishSensitive: true
    },
    softwood: {
      name: "Softwood",
      chipMin: 0.12, chipMax: 0.35, chipDefault: 0.22,
      vMin: 300, vMax: 650, vDefault: 480,
      heatSensitive: false, finishSensitive: false
    },
    hardwood: {
      name: "Hardwood (e.g. Oak)",
      chipMin: 0.10, chipMax: 0.26, chipDefault: 0.16,
      vMin: 260, vMax: 550, vDefault: 420,
      heatSensitive: false, finishSensitive: true
    },
    custom: {
      name: "Custom",
      chipMin: 0.08, chipMax: 0.26, chipDefault: 0.14,
      vMin: 220, vMax: 600, vDefault: 380,
      heatSensitive: false, finishSensitive: false
    }
  };

  // ---------------- cutter types ----------------
  // Keys should match <option value="..."> in your Liquid, but we also normalize common aliases.
  var CUTTERS = {
    upcut:      { name: "Upcut spiral",       chipBias: 1.00, rpmBias: 1.00, finishBias: 0.95, microTip: false },
    downcut:    { name: "Downcut spiral",     chipBias: 0.95, rpmBias: 0.95, finishBias: 1.10, microTip: false },
    compression:{ name: "Compression",        chipBias: 0.90, rpmBias: 0.95, finishBias: 1.15, microTip: false },
    straight:   { name: "Straight flute",     chipBias: 0.95, rpmBias: 0.90, finishBias: 1.00, microTip: false },
    ballnose:   { name: "Ball nose",          chipBias: 0.85, rpmBias: 0.95, finishBias: 1.15, microTip: false },
    tapered_ballnose:{ name:"Tapered ball nose", chipBias:0.80, rpmBias:0.90, finishBias:1.15, microTip:false },
    vbit:       { name: "V-bit",              chipBias: 0.45, rpmBias: 0.80, finishBias: 1.20, microTip: true  },
    engraving:  { name: "Engraving",          chipBias: 0.40, rpmBias: 0.80, finishBias: 1.20, microTip: true  },
    drill:      { name: "Drill",              chipBias: 0.55, rpmBias: 0.75, finishBias: 1.00, microTip: false },
    form:       { name: "Form tool",          chipBias: 0.75, rpmBias: 0.85, finishBias: 1.10, microTip: false }
  };

  var FINISH = {
    roughing: { name: "Roughing", chipMult: 1.15, rpmMult: 0.95 },
    standard: { name: "Standard", chipMult: 1.00, rpmMult: 1.00 },
    clean:    { name: "Clean",    chipMult: 0.80, rpmMult: 1.05 }
  };

  var RIGIDITY = {
    light:      { name: "Light",      chipMult: 0.75, rpmMult: 0.95 },
    mid:        { name: "Mid-range",  chipMult: 1.00, rpmMult: 1.00 },
    industrial: { name: "Industrial", chipMult: 1.10, rpmMult: 1.05 }
  };

  function normalizeCutterKey(k) {
    if (!k) return "upcut";
    // map newer/alternate keys if they appear
    if (k === "endmill_upcut") return "upcut";
    if (k === "endmill_downcut") return "downcut";
    if (k === "endmill_compression") return "compression";
    if (k === "endmill_straight") return "straight";
    return k;
  }

  // ---------------- geometry helpers (optional inputs) ----------------
  function getGeometry(container) {
    // These fields may or may not exist in your markup; JS must tolerate nulls.
    function q(sel) { return container.querySelector(sel); }
    var includedAngle = safeNum(q("[data-cnc-included-angle]") && q("[data-cnc-included-angle]").value, 90);
    var sideAngle     = safeNum(q("[data-cnc-side-angle]") && q("[data-cnc-side-angle]").value, 15);
    var flatDiameter  = safeNum(q("[data-cnc-flat-diameter]") && q("[data-cnc-flat-diameter]").value, 0);
    var tipRadius     = safeNum(q("[data-cnc-tip-radius]") && q("[data-cnc-tip-radius]").value, 0);
    var depth         = safeNum(q("[data-cnc-depth]") && q("[data-cnc-depth]").value, 0);
    var useEffective  = !!(q("[data-cnc-use-effective-diameter]") && q("[data-cnc-use-effective-diameter]").checked);

    return {
      includedAngle: includedAngle,
      sideAngle: sideAngle,
      flatDiameter: flatDiameter,
      tipRadius: tipRadius,
      depth: depth,
      useEffective: useEffective
    };
  }

  function effectiveDiameterMm(cutterKey, diameterMm, geo) {
    var D = diameterMm;
    if (!D || D <= 0) return 0;
    var depth = geo && geo.depth ? geo.depth : 0;

    // If no depth given, we can’t compute engagement; fall back to flat tip if provided.
    if (!depth || depth <= 0) {
      if ((cutterKey === "engraving" || cutterKey === "vbit") && geo && geo.flatDiameter > 0) {
        return clamp(geo.flatDiameter, 0.05, D);
      }
      return D;
    }

    if (cutterKey === "vbit") {
      var inc = (geo && geo.includedAngle ? geo.includedAngle : 90) * (PI / 180);
      var half = inc / 2;
      var de = 2 * Math.tan(half) * depth;
      if (geo && geo.flatDiameter > 0) de = Math.max(de, geo.flatDiameter);
      return clamp(de, 0.05, D);
    }

    if (cutterKey === "engraving") {
      // Side angle is typically half-angle from vertical for engraving bits in many UIs.
      var a = (geo && geo.sideAngle ? geo.sideAngle : 15) * (PI / 180);
      var de2 = 2 * Math.tan(a) * depth;
      if (geo && geo.flatDiameter > 0) de2 = Math.max(de2, geo.flatDiameter);
      return clamp(de2, 0.05, D);
    }

    if (cutterKey === "tapered_ballnose") {
      var a2 = (geo && geo.sideAngle ? geo.sideAngle : 10) * (PI / 180);
      var R = (geo && geo.tipRadius ? geo.tipRadius : 0);
      var de3 = 2 * (R + Math.tan(a2) * depth);
      return clamp(de3, 0.2, D);
    }

    return D;
  }

  // ---------------- recommendations ----------------
  function recommendSurfaceSpeed(matKey, cutterKey, finishKey, rigidityKey) {
    var mat = MATERIALS[matKey] || MATERIALS.mdf;
    var cutter = CUTTERS[cutterKey] || CUTTERS.upcut;
    var fin = FINISH[finishKey] || FINISH.standard;
    var rig = RIGIDITY[rigidityKey] || RIGIDITY.mid;

    var v = mat.vDefault * cutter.rpmBias * fin.rpmMult * rig.rpmMult;
    return clamp(v, mat.vMin, mat.vMax);
  }

  function recommendStepoverPct(finishKey) {
    if (finishKey === "roughing") return 45;
    if (finishKey === "clean") return 12;
    return 30;
  }

  function recommendStepdown(diameterMm, cutterKey, rigidityKey, geo) {
    var D = clamp(diameterMm || 6, 1, 25);
    var rig = RIGIDITY[rigidityKey] || RIGIDITY.mid;

    // Engraving / V-bit: cap aggressively (0.1–0.5 mm max)
    if (cutterKey === "vbit" || cutterKey === "engraving") {
      var userDepth = geo && geo.depth > 0 ? geo.depth : 0;
      var suggested = userDepth > 0
        ? Math.min(userDepth, 0.5)
        : Math.min(0.3, 0.10 * D);
      return clamp(suggested, 0.1, 0.5);
    }

    // Tapered ballnose: less strict but cap at 1.0 mm
    if (cutterKey === "tapered_ballnose") {
      var baseTb = D * 0.50 * rig.chipMult;
      return clamp(Math.min(baseTb, 1.0), 0.2, 1.0);
    }

    var base = D * 0.75; // general routing
    if (cutterKey === "ballnose") base = D * 0.50;
    if (cutterKey === "drill") base = D * 1.00;

    base *= rig.chipMult;
    return clamp(base, 0.2, 18);
  }

  // Main fix: engraving/vbit chipload must be based on effective diameter and aggressively capped.
  function recommendChipLoad(matKey, cutterKey, diameterMm, effectiveMm, flutes, finishKey, rigidityKey) {
    var mat = MATERIALS[matKey] || MATERIALS.mdf;
    var cutter = CUTTERS[cutterKey] || CUTTERS.upcut;
    var fin = FINISH[finishKey] || FINISH.standard;
    var rig = RIGIDITY[rigidityKey] || RIGIDITY.mid;

    var z = Math.max(1, flutes || 2);
    var fluteScale = (z === 1) ? 1.15 : (z === 2) ? 1.00 : (z === 3) ? 0.90 : 0.80;

    // Micro-tip tools (V-bit / engraving): treat as micro cutting edge, not as 6mm router bit.
    if (cutter.microTip) {
      var De = clamp(effectiveMm || Math.min(diameterMm || 6, 0.5), 0.05, diameterMm || 6);

      // Base micro chipload tuned to feel "credible" in timber:
      // - at ~0.2mm effective diameter => ~0.02 mm/tooth
      // - scale gently with sqrt
      var baseMicro = 0.02 * Math.sqrt(De / 0.2);

      // Finish & rigidity influence, but keep bounded
      var chip = baseMicro * fin.chipMult * rig.chipMult;

      // Flute scaling still applies
      chip *= fluteScale;

      // Clamp aggressively for safety in hardwood/panel engraving
      // Typical practical band: 0.005–0.03 mm/tooth
      return clamp(chip, 0.005, 0.03);
    }

    // Standard routing cutters: scale with diameter
    var D = clamp(diameterMm || 6, 2, 20);
    var base = mat.chipDefault * Math.sqrt(D / 8);
    base *= fluteScale * cutter.chipBias * fin.chipMult * rig.chipMult;

    var min = mat.chipMin * Math.sqrt(D / 8);
    var max = mat.chipMax * Math.sqrt(D / 8);

    return clamp(base, min, max);
  }

  // ---------------- SVG (compact, horizontal, tied to cutter) ----------------
  function cutterSvg(cutterKey, flutes) {
    var w = 140, h = 60, cy = 30;
    var stripes = Math.min(4, Math.max(2, flutes || 2));
    var id = ("cablab-" + cutterKey + "-" + stripes).replace(/[^a-z0-9_-]/gi, "");

    function stripesClipped(bodyPath) {
      var clipId = "clip-" + id;
      var clip = '<defs><clipPath id="' + clipId + '"><path d="' + bodyPath + '"/></clipPath></defs>';
      var s = "";
      for (var i = 0; i < stripes; i++) {
        var x0 = 44 + i * 18;
        s += '<path d="M ' + x0 + ' ' + (cy - 16) + ' L ' + (x0 + 16) + ' ' + (cy + 16) + '" ' +
             'stroke="var(--cablab-accent)" stroke-width="2.3" stroke-linecap="round" opacity="0.85"/>';
      }
      return clip + '<g clip-path="url(#' + clipId + ')">' + s + '</g>';
    }

    var defs =
      '<defs>' +
        '<linearGradient id="metal-' + id + '" x1="0" y1="0" x2="1" y2="0">' +
          '<stop offset="0" stop-color="var(--cablab-text-muted)" stop-opacity="0.58"/>' +
          '<stop offset="0.5" stop-color="var(--cablab-text-muted)" stop-opacity="0.28"/>' +
          '<stop offset="1" stop-color="var(--cablab-text-muted)" stop-opacity="0.58"/>' +
        '</linearGradient>' +
      '</defs>';

    var shank = '<rect x="10" y="' + (cy - 10) + '" width="22" height="20" rx="4" fill="var(--cablab-text-muted)" opacity="0.40"/>';

    if (cutterKey === "vbit") {
      var bodyPath = 'M 40 ' + (cy - 12) + ' L 132 ' + (cy - 1.4) + ' L 132 ' + (cy + 1.4) + ' L 40 ' + (cy + 12) + ' Z';
      return '<svg viewBox="0 0 ' + w + ' ' + h + '" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        defs + shank +
        '<path d="' + bodyPath + '" fill="url(#metal-' + id + ')" stroke="var(--cablab-text-muted)" stroke-opacity="0.30" stroke-width="1"/>' +
        stripesClipped(bodyPath) +
        '<circle cx="132" cy="' + cy + '" r="1.6" fill="var(--cablab-accent)" opacity="0.95"/>' +
      '</svg>';
    }

    if (cutterKey === "engraving") {
      var bodyPath2 = 'M 40 ' + (cy - 11) + ' L 132 ' + (cy - 3) + ' L 132 ' + (cy + 3) + ' L 40 ' + (cy + 11) + ' Z';
      return '<svg viewBox="0 0 ' + w + ' ' + h + '" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        defs + shank +
        '<path d="' + bodyPath2 + '" fill="url(#metal-' + id + ')" stroke="var(--cablab-text-muted)" stroke-opacity="0.30" stroke-width="1"/>' +
        stripesClipped(bodyPath2) +
        '<line x1="132" y1="' + (cy - 3) + '" x2="132" y2="' + (cy + 3) + '" stroke="var(--cablab-accent)" stroke-width="2" opacity="0.9"/>' +
      '</svg>';
    }

    // Default endmill-like
    var bodyPath3 = 'M 38 ' + (cy - 12) + ' L 132 ' + (cy - 12) + ' L 132 ' + (cy + 12) + ' L 38 ' + (cy + 12) + ' Z';
    var extra = "";
    if (cutterKey === "compression") {
      // split chevrons to hint compression
      extra = '<path d="M 58 ' + (cy - 12) + ' L 76 ' + cy + ' L 58 ' + (cy + 12) + '" stroke="var(--cablab-accent)" stroke-width="2.2" opacity="0.85" fill="none" stroke-linecap="round"/>' +
              '<path d="M 76 ' + (cy - 12) + ' L 58 ' + cy + ' L 76 ' + (cy + 12) + '" stroke="var(--cablab-accent)" stroke-width="2.2" opacity="0.85" fill="none" stroke-linecap="round"/>';
      return '<svg viewBox="0 0 ' + w + ' ' + h + '" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
        defs + shank +
        '<path d="' + bodyPath3 + '" fill="url(#metal-' + id + ')" stroke="var(--cablab-text-muted)" stroke-opacity="0.30" stroke-width="1"/>' +
        extra +
      '</svg>';
    }

    return '<svg viewBox="0 0 ' + w + ' ' + h + '" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      defs + shank +
      '<path d="' + bodyPath3 + '" fill="url(#metal-' + id + ')" stroke="var(--cablab-text-muted)" stroke-opacity="0.30" stroke-width="1"/>' +
      stripesClipped(bodyPath3) +
    '</svg>';
  }

  // ---------------- main tool init ----------------
  function init(container) {
    if (!container) return;

    var geometryToggle = container.querySelector('[data-cnc-geometry]');
    var geometryPanel  = container.querySelector('[data-cnc-geometry-panel]');

    var geoFieldIncluded = container.querySelector('[data-cnc-field-included-angle]');
    var geoFieldSide     = container.querySelector('[data-cnc-field-side-angle]');
    var geoFieldFlat     = container.querySelector('[data-cnc-field-flat-diameter]');
    var geoFieldTipR     = container.querySelector('[data-cnc-field-tip-radius]');
    var geoEffectiveWrap = container.querySelector('[data-cnc-effective-diameter]');


    var materialSelect  = container.querySelector("[data-cnc-material]");
    var diameterInput   = container.querySelector("[data-cnc-diameter]");
    var flutesSelect    = container.querySelector("[data-cnc-flutes]");
    var cutterSelect    = container.querySelector("[data-cnc-cutter]");
    var finishSelect    = container.querySelector("[data-cnc-finish]");
    var rigiditySelect  = container.querySelector("[data-cnc-rigidity]");
    var spindleMaxInput = container.querySelector("[data-cnc-spindle-max]");

    var imperialToggle  = container.querySelector("[data-cnc-imperial]");

    var advancedToggle  = container.querySelector("[data-cnc-advanced-toggle]");
    var advancedPanel   = container.querySelector("[data-cnc-advanced-panel]");
    var chipOverrideInput   = container.querySelector("[data-cnc-chip-override]");
    var surfaceSpeedInput   = container.querySelector("[data-cnc-surface-speed]");
    var stepdownInput       = container.querySelector("[data-cnc-stepdown]");
    var stepoverInput       = container.querySelector("[data-cnc-stepover]");

    var resultRpm      = container.querySelector("[data-cnc-result-rpm]");
    var resultFeed     = container.querySelector("[data-cnc-result-feed]");
    var resultChip     = container.querySelector("[data-cnc-result-chip]");
    var resultSurface  = container.querySelector("[data-cnc-result-surface]");
    var resultMrr      = container.querySelector("[data-cnc-result-mrr]");
    var resultStepdown = container.querySelector("[data-cnc-result-stepdown]");
    var resultStepover = container.querySelector("[data-cnc-result-stepover]");
    var resultPlunge   = container.querySelector("[data-cnc-result-plunge]");
    var resultSurfaceLabel = container.querySelector("[data-cnc-surface-label]");
    var resultSurfaceSublabel = container.querySelector("[data-cnc-surface-sublabel]");
    var warningsEl     = container.querySelector("[data-cnc-warnings]");

    var unitDiameter = container.querySelector("[data-cnc-unit-diameter]");
    var unitPlunge   = container.querySelector("[data-cnc-unit-plunge]");
    var unitFeed     = container.querySelector("[data-cnc-unit-feed]");
    var unitChip     = container.querySelector("[data-cnc-unit-chip]");
    var unitStepdown = container.querySelector("[data-cnc-unit-stepdown]");

    var cutterSvgEl = container.querySelector("[data-cnc-cutter-svg]");

    var isImperial = !!(imperialToggle && imperialToggle.checked);

    function applyUnits() {
      if (unitDiameter) unitDiameter.textContent = isImperial ? "in" : "mm";
      if (unitFeed) unitFeed.textContent = isImperial ? "in/min" : "mm/min";
      if (unitChip) unitChip.textContent = isImperial ? "in/tooth" : "mm/tooth";
      if (unitStepdown) unitStepdown.textContent = isImperial ? "in" : "mm";
      if (unitPlunge) unitPlunge.textContent = isImperial ? "in/min" : "mm/min";
    }

    function run() {
      var materialKey = (materialSelect && materialSelect.value) || "mdf";
      var cutterKeyRaw = (cutterSelect && cutterSelect.value) || "upcut";
      var cutterKey = normalizeCutterKey(cutterKeyRaw);

      var finishKey = (finishSelect && finishSelect.value) || "standard";
      var rigidityKey = (rigiditySelect && rigiditySelect.value) || "mid";

      var mat = MATERIALS[materialKey] || MATERIALS.mdf;
      var cutter = CUTTERS[cutterKey] || CUTTERS.upcut;

      var diaRaw = safeNum(diameterInput && diameterInput.value, 6);
      var diameterMm = isImperial ? inToMm(diaRaw) : diaRaw;
      diameterMm = clamp(diameterMm, 0.5, 50);

      var flutes = parseInt(flutesSelect && flutesSelect.value, 10) || 2;
      flutes = clamp(flutes, 1, 8);

      var spindleMax = spindleMaxInput && spindleMaxInput.value
        ? parseInt(spindleMaxInput.value, 10)
        : 24000;

      // Geometry (optional)
      var geo = getGeometry(container);

      // Always compute effective diameter for micro-tip tools for better chipload defaults.
      // If user has no geometry fields, it will fall back to provided diameter.
      var effMm = cutter.microTip ? effectiveDiameterMm(cutterKey, diameterMm, geo) : diameterMm;

      // Surface speed override (m/min)
      var surfaceOverride = safeNum(surfaceSpeedInput && surfaceSpeedInput.value, 0);
      var surfaceSpeed = surfaceOverride > 0
        ? surfaceOverride
        : recommendSurfaceSpeed(materialKey, cutterKey, finishKey, rigidityKey);

      // Chipload override (mm/tooth) — if imperial, the override is in inches/tooth
      var chipOverride = safeNum(chipOverrideInput && chipOverrideInput.value, 0);
      var chipLoadMm = chipOverride > 0
        ? (isImperial ? inToMm(chipOverride) : chipOverride)
        : recommendChipLoad(materialKey, cutterKey, diameterMm, effMm, flutes, finishKey, rigidityKey);

      // Stepover/stepdown
      var stepover = safeNum(stepoverInput && stepoverInput.value, 0);
      stepover = stepover > 0 ? clamp(stepover, 1, 95) : recommendStepoverPct(finishKey);

      var stepdownOverride = safeNum(stepdownInput && stepdownInput.value, 0);
      var stepdownMm = stepdownOverride > 0
        ? (isImperial ? inToMm(stepdownOverride) : stepdownOverride)
        : recommendStepdown(diameterMm, cutterKey, rigidityKey, geo);

      // RPM
      var rpm = Math.round(rpmFromSurfaceSpeed(surfaceSpeed, cutter.microTip ? effMm : diameterMm));
      if (spindleMax > 0 && rpm > spindleMax) rpm = spindleMax;
      rpm = clamp(rpm, 1000, 30000);

      // Feed
      var feedMmMin = Math.round(feedRateMmMin(chipLoadMm, flutes, rpm));

      // Plunge rate (micro-tip: 20% of feed, 100–600 mm/min; others: 35% of feed, 200–2500 mm/min)
      var plungeMmMin;
      if (cutterKey === "vbit" || cutterKey === "engraving") {
        plungeMmMin = Math.round(clamp(feedMmMin * 0.20, 100, 600));
      } else {
        plungeMmMin = Math.round(clamp(feedMmMin * 0.35, 200, 2500));
      }
      if (materialKey === "hardwood") plungeMmMin = Math.round(plungeMmMin * 0.9);

      // Derived
      var actualSurface = surfaceSpeedFromRpm(rpm, cutter.microTip ? effMm : diameterMm);
      var mrrVal = mrr(feedMmMin, stepdownMm, stepover);

      // Warnings
      var fin = FINISH[finishKey] || FINISH.standard;
      var warnings = [];

      // Micro-tip tool sanity warnings
      if (cutter.microTip) {
        if (effMm <= 0.25) warnings.push("Micro tip engagement detected — keep chip load low and avoid aggressive plunges.");
        if (chipLoadMm > 0.03) warnings.push("Chip load is high for engraving/V-bit — risk of tip breakage.");
        if (chipLoadMm < 0.006) warnings.push("Chip load very low — rubbing/burning possible; consider reducing RPM or increasing feed slightly.");
        if (geo && geo.depth > 0 && !geo.useEffective) {
          warnings.push("Tip tools: enable 'effective diameter' (if available) for more accurate RPM at depth.");
        }
      } else {
        // Rubbing / burning risk for routing
        if (mat.heatSensitive) {
          var burnThreshold = mat.chipDefault * 0.65;
          if (chipLoadMm < burnThreshold && rpm > 14000) warnings.push("Risk of burning — increase chip load or reduce RPM.");
        }
        if (materialKey === "mfc" && cutterKey !== "compression" && cutterKey !== "downcut") {
          warnings.push("MFC: consider compression or downcut for cleaner top face.");
        }
        if (mat.finishSensitive && fin.name === "Clean" && cutter.finishBias < 1.0) {
          warnings.push("Clean finish: downcut/compression often improves the top surface.");
        }
      }

      // Units + outputs
      applyUnits();

      if (resultRpm) resultRpm.textContent = rpm.toLocaleString();

      if (isImperial) {
        if (resultFeed) resultFeed.textContent = fmt(mmMinToInMin(feedMmMin), 1);
        if (resultChip) resultChip.textContent = fmt(mmToIn(chipLoadMm), 4);
        if (resultStepdown) resultStepdown.textContent = fmt(mmToIn(stepdownMm), 3);
        if (resultPlunge) resultPlunge.textContent = fmt(mmMinToInMin(plungeMmMin), 2);
      } else {
        if (resultFeed) resultFeed.textContent = feedMmMin.toLocaleString();
        if (resultChip) resultChip.textContent = fmt(chipLoadMm, 3);
        if (resultStepdown) resultStepdown.textContent = fmt(stepdownMm, 1);
        if (resultPlunge) resultPlunge.textContent = plungeMmMin.toLocaleString();
      }

      if (resultSurface) resultSurface.textContent = fmt(actualSurface, 0) + " m/min";
      if (resultMrr) resultMrr.textContent = fmt(mrrVal, 0) + " mm³/min";
      if (resultStepover) resultStepover.textContent = stepover + "%";

      if (resultSurfaceLabel && resultSurfaceSublabel) {
        if (cutter.microTip) {
          resultSurfaceLabel.textContent = "Tip surface speed (at depth)";
          resultSurfaceSublabel.textContent = "Calculated using effective diameter.";
        } else {
          resultSurfaceLabel.textContent = "Surface speed";
          resultSurfaceSublabel.textContent = "Tool surface velocity";
        }
      }

      if (warningsEl) {
        warningsEl.innerHTML = warnings.length ? "<ul><li>" + warnings.join("</li><li>") + "</li></ul>" : "";
        warningsEl.classList.toggle("cablab-cnc__warnings--empty", !warnings.length);
      }

      if (cutterSvgEl) {
        cutterSvgEl.innerHTML = cutterSvg(cutterKey, flutes);
      }
    }

    function addListeners(el) {
      if (!el) return;
      el.addEventListener("input", run);
      el.addEventListener("change", run);
    }

    [
      materialSelect, diameterInput, flutesSelect, cutterSelect, finishSelect, rigiditySelect, spindleMaxInput,
      chipOverrideInput, surfaceSpeedInput, stepdownInput, stepoverInput,
      container.querySelector("[data-cnc-included-angle]"),
      container.querySelector("[data-cnc-side-angle]"),
      container.querySelector("[data-cnc-flat-diameter]"),
      container.querySelector("[data-cnc-tip-radius]"),
      container.querySelector("[data-cnc-depth]"),
      container.querySelector("[data-cnc-use-effective-diameter]")
    ].forEach(addListeners);

    if (imperialToggle) {
      imperialToggle.addEventListener("change", function () {
        isImperial = imperialToggle.checked;
        applyUnits();
        run();
      });
    }

    if (advancedToggle && advancedPanel) {
      advancedToggle.addEventListener("click", function () {
        var isOpen = advancedPanel.classList.toggle("cablab-cnc__advanced--open");
        advancedToggle.setAttribute("aria-expanded", isOpen);
        var icon = advancedToggle.querySelector(".cablab-cnc__advanced-toggle-icon");
        if (icon) icon.textContent = isOpen ? "▾" : "▸";
      });
    }

    if (geometryToggle && geometryPanel) {
      geometryToggle.addEventListener("change", function () {
        geometryPanel.classList.toggle("cablab-cnc__geometry--open", geometryToggle.checked);
        run();
      });
    }

// Update geometry fields when cutter changes
if (cutterSelect) {
  cutterSelect.addEventListener('change', function () {
    setGeometryFieldsForCutter(cutterSelect.value);
    run();
  });

  // Initial field visibility
  setGeometryFieldsForCutter(cutterSelect.value);
}


function setGeometryFieldsForCutter(cutterKey) {
  // Default: hide all, then enable what’s relevant
  function show(el, yes) { if (el) el.style.display = yes ? '' : 'none'; }

  // Normalise endmill group keys from Liquid
  // (Your select uses endmill_upcut/endmill_downcut/etc.)
  var key = cutterKey || 'endmill_upcut';

  var isVBit = (key === 'vbit');
  var isEngraving = (key === 'engraving');
  var isDrill = (key === 'drill');
  var isTaperedBall = (key === 'tapered_ballnose');

  // Included angle: V-bit and Drill (118° etc.)
  show(geoFieldIncluded, isVBit || isDrill);

  // Side angle: Engraving + Tapered ballnose (taper angle)
  show(geoFieldSide, isEngraving || isTaperedBall);

  // Flat diameter: V-bit + Engraving (tip width / flat)
  show(geoFieldFlat, isVBit || isEngraving);

  // Tip radius: Tapered ballnose only
  show(geoFieldTipR, isTaperedBall);

  // Effective diameter area (depth + checkbox): only for tip-sensitive tools
  show(geoEffectiveWrap, isVBit || isEngraving || isTaperedBall);
}


    applyUnits();
    run();
  }

  function boot() {
    document.querySelectorAll("[data-cablab-cnc-feeds-speeds]").forEach(init);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
